// cn util
import { ClassValue, clsx } from 'clsx';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Layout Component
type LayoutProps = {
  children: React.ReactNode;
  className?: string;
};

const Layout = ({ children, className }: LayoutProps) => {
  return (
    <html
      lang="en"
      suppressHydrationWarning
      className={cn('scroll-smooth antialiased focus:scroll-auto', className)}>
      {children}
    </html>
  );
};

// Main Component
type MainProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
};

const Main = ({ children, className, id }: MainProps) => {
  return (
    <main
      className={cn(
        // `Main` Specific Styles
        'max-w-none prose-p:m-0',
        // General Prose
        'prose prose-neutral prose:font-sans dark:prose-invert xl:prose-lg',
        // Prose Headings
        'prose-headings:font-normal',
        // Prose Strong
        'prose-strong:font-semibold',
        // Inline Links
        'prose-a:underline prose-a:decoration-primary/50 prose-a:underline-offset-2 prose-a:text-foreground/75 prose-a:transition-all',
        // Inline Link Hover
        'hover:prose-a:decoration-primary hover:prose-a:text-foreground',
        // Blockquotes
        'prose-blockquote:not-italic',
        // Pre and Code Blocks
        'prose-pre:border prose-pre:bg-muted/25 prose-pre:text-foreground',
        className,
      )}
      id={id}>
      {children}
    </main>
  );
};

// Section Component
type SectionProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
};

const Section = ({ children, className, id }: SectionProps) => {
  return (
    <section className={cn('py-8 md:py-12 fade-in', className)} id={id}>
      {children}
    </section>
  );
};

// Container Component
type ContainerProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
};

const Container = ({ children, className, id }: ContainerProps) => {
  return (
    <div className={cn('mx-auto max-w-5xl', 'p-6 sm:p-8', className)} id={id}>
      {children}
    </div>
  );
};

// Article Component
type ArticleProps = {
  children?: React.ReactNode;
  className?: string;
  id?: string;
  dangerouslySetInnerHTML?: { __html: string };
};

const Article = ({
  children,
  className,
  id,
  dangerouslySetInnerHTML,
}: ArticleProps) => {
  return (
    <article
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      className={cn(
        // General Prose
        'prose prose-neutral prose:font-sans xl:prose-lg',
        // Prose Headings
        'prose-headings:font-normal',
        // Prose Paragraphs
        'prose-p:mb-2',
        // Prose Strong
        'prose-strong:font-semibold',
        // Inline Links
        'prose-a:underline prose-a:decoration-primary/50 prose-a:underline-offset-2 prose-a:text-foreground/75 prose-a:transition-all',
        // Inline Link Hover
        'hover:prose-a:decoration-primary hover:prose-a:text-foreground',
        // Blockquotes
        'prose-blockquote:not-italic',
        // Pre and Code Blocks
        'prose-pre:border prose-pre:bg-muted/25',
        // Images
        'prose-img:rounded-lg prose-img:border prose-img:overflow-hidden',
        className,
      )}
      id={id}>
      {children}
    </article>
  );
};

export { Layout, Main, Section, Container, Article };
